import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import '@fontsource-variable/inter';
import '@fontsource-variable/nunito';
import './styles.css';
import { App } from './App';
import { AcceptInvitePage } from './auth/AcceptInvitePage';
import { AuthCallback } from './auth/AuthCallback';
import { SignupPage } from './auth/Signup';
import { LoginPage } from './auth/Login';
import { ForgotPasswordPage } from './auth/ForgotPassword';
import { bootRefresh } from './auth/bootRefresh';
import { applyAppearancePrefs, loadAppearancePrefs } from './lib/preferences';
// Imported for its side effect: initializes i18next + sets up the
// language detector before any component calls `useTranslation`.
import './lib/i18n';
import { RequireAuth } from './auth/RequireAuth';
import { DashboardLayout } from './dashboard/DashboardLayout';
import { ChildrenPage } from './dashboard/ChildrenPage';
import { CirclePage } from './dashboard/CirclePage';
import { SafetyPage } from './dashboard/SafetyPage';
import { ScreenTimePage } from './dashboard/ScreenTimePage';
import { RewardsPage } from './dashboard/RewardsPage';
import { DrivingPage } from './dashboard/DrivingPage';
import { CalendarPage } from './dashboard/CalendarPage';
import { SafeIdPage } from './dashboard/SafeIdPage';
import { PetsPage } from './dashboard/PetsPage';
import { ScamShieldPage } from './dashboard/ScamShieldPage';
import { EmergencyPage } from './dashboard/EmergencyPage';
import { SosPage } from './dashboard/SosPage';
import { SettingsPage } from './dashboard/SettingsPage';
import { BillingPage } from './dashboard/BillingPage';
import { TimelinePage } from './dashboard/TimelinePage';
import { ChatPage } from './dashboard/ChatPage';

// Map page pulls in maplibre-gl (~700 KB) — defer until route is visited.
const MapPage = lazy(() =>
  import('./map/MapPage').then((m) => ({ default: m.MapPage })),
);

function MapLoading() {
  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center text-sm text-zinc-500">
      Loading map…
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 2, refetchOnWindowFocus: false },
  },
});

// Apply stored appearance prefs synchronously before first paint so
// dark-mode users don't flash light mode on hard reload.
applyAppearancePrefs(loadAppearancePrefs());

// Refresh the access token from the HttpOnly cookie BEFORE first render so a
// hard reload of any protected page doesn't flash as "signed out".
bootRefresh().finally(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/invite/:token" element={<AcceptInvitePage />} />
            <Route
              element={
                <RequireAuth>
                  <DashboardLayout />
                </RequireAuth>
              }
            >
              <Route path="/" element={<App />} />
              <Route
                path="/map"
                element={
                  <Suspense fallback={<MapLoading />}>
                    <MapPage />
                  </Suspense>
                }
              />
              <Route path="/children" element={<ChildrenPage />} />
              <Route path="/safety" element={<SafetyPage />} />
              <Route path="/screen-time" element={<ScreenTimePage />} />
              <Route path="/rewards" element={<RewardsPage />} />
              <Route path="/driving" element={<DrivingPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/safe-id" element={<SafeIdPage />} />
              <Route path="/pets" element={<PetsPage />} />
              <Route path="/scam-shield" element={<ScamShieldPage />} />
              <Route path="/emergency" element={<EmergencyPage />} />
              <Route path="/sos" element={<SosPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/timeline" element={<TimelinePage />} />
              <Route path="/circle" element={<CirclePage />} />
              <Route path="/chat" element={<ChatPage />} />
            </Route>
            <Route
              path="*"
              element={
                <RequireAuth>
                  <App />
                </RequireAuth>
              }
            />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
});
